import { createContext, useContext, useEffect, useState } from "react";
import api, { TOKEN_KEY, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, object=auth, false=guest
  const [company, setCompany] = useState(null);

  const loadCompany = async () => {
    try {
      const { data } = await api.get("/companies/me");
      setCompany(data.company);
      return data.company;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => { setUser(res.data); loadCompany(); })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      const me = await api.get("/auth/me");
      setUser(me.data);
      await loadCompany();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const signup = async (payload) => {
    try {
      const { data } = await api.post("/companies/signup", payload);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      const me = await api.get("/auth/me");
      setUser(me.data);
      await loadCompany();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    setCompany(null);
  };

  const hasPerm = (key) => {
    const keys = user?.permission_keys || [];
    return keys.includes("*") || keys.includes("system:admin") || keys.includes(key);
  };

  return (
    <AuthContext.Provider value={{ user, company, login, signup, logout, hasPerm, refreshCompany: loadCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
